import { RhfDropdown } from '@taxbit-private/cosmic-react-hook-form'
import { useCallback, useMemo, useState } from 'react'

import { useOrgDisplayWithSlug } from 'hooks/utils/useOrgDisplayWithSlug'
import { useDynamicLabels } from 'layouts/app/useDynamicLabels'

import { EntityOption, Entities } from './hooks/useOrgLegalEntities'
import { OrgManagerFormValues } from './hooks/useOrgManagerFormMethods'

enum EntitySelectorDropdownTrackingId {
  Dropdown = 'entity-selector-dropdown',
}

const filterEntityOptions = (
  options: EntityOption[],
  getLabel: (option: EntityOption) => string,
  getDescription: (option: EntityOption) => string,
  query: string
): EntityOption[] => {
  if (!query.trim()) {
    return options
  }
  const lowerQuery = query.toLowerCase()
  return options.filter((option) => {
    const labelMatch = getLabel(option).toLowerCase().includes(lowerQuery)
    const descMatch = getDescription(option).toLowerCase().includes(lowerQuery)
    const valueMatch = option.orgName.toLowerCase().includes(lowerQuery)
    return labelMatch || descMatch || valueMatch
  })
}

type EntitySelectorDropdownProps = {
  selectedOrgId?: string
  entities: Entities
  isLoading: boolean
}

const EntitySelectorDropdown = ({
  selectedOrgId,
  entities,
  isLoading,
}: EntitySelectorDropdownProps) => {
  const [entitySearchTerm, setEntitySearchTerm] = useState('')
  const { legalEntitiesLabel, legalEntityLabel } = useDynamicLabels()
  const { shouldShowSlug } = useOrgDisplayWithSlug()

  const getEntityOptionLabel = useCallback((option: EntityOption) => {
    return option.displayName ?? option.orgName
  }, [])
  const getEntityOptionDescription = useCallback(
    (option: EntityOption) => {
      if (shouldShowSlug && option.orgSlug) {
        return option.orgSlug
      }
      return ''
    },
    [shouldShowSlug]
  )
  const getEntityOptionValue = useCallback((option: EntityOption) => option.orgName, [])
  const getEntityOptionKey = useCallback((option: EntityOption) => option.orgName, [])

  const filteredEntityOptions = useMemo(
    () =>
      filterEntityOptions(
        entities,
        getEntityOptionLabel,
        getEntityOptionDescription,
        entitySearchTerm
      ),
    [entities, getEntityOptionLabel, getEntityOptionDescription, entitySearchTerm]
  )

  const hasEntities = entities.length > 0
  const isDisabled = !selectedOrgId || !hasEntities

  const placeholder = selectedOrgId
    ? hasEntities
      ? `Select a ${legalEntityLabel.toLowerCase()}...`
      : `No ${legalEntitiesLabel.toLowerCase()} available`
    : 'Select an organization first'

  return (
    <RhfDropdown<OrgManagerFormValues, 'legalEntityId', EntityOption>
      label="Legal Entity"
      name="legalEntityId"
      getOptionValue={getEntityOptionValue}
      getOptionKey={getEntityOptionKey}
      getOptionLabel={getEntityOptionLabel}
      getOptionDescription={getEntityOptionDescription}
      options={filteredEntityOptions}
      trackingId={EntitySelectorDropdownTrackingId.Dropdown}
      shouldUsePortal={true}
      isLoading={isLoading}
      searchBarProps={{
        placeholder: 'Search...',
        value: entitySearchTerm,
        onTextChange: setEntitySearchTerm,
      }}
      isDisabled={isDisabled}
      placeholder={placeholder}
    />
  )
}

export default EntitySelectorDropdown
